import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn } from '@reduxjs/toolkit/query';
import LogRocket from 'logrocket';

import { baseUrl } from '../utils/config';
import tokenManager from '../utils/tokenManager';
import { resetAuth } from './auth';
import { setError } from './error';

/*
  when query result is still in cache, baseQuery will not be called to
  refetch.
*/
const baseQuery = fetchBaseQuery({
	baseUrl,
	prepareHeaders: async (headers) => {
		const getAccessToken = tokenManager.getAccessTokenSilently();

		// Check if getAccessToken is not null or undefined
		if (getAccessToken) {
			const token = await getAccessToken();

			if (token) {
				headers.set('Authorization', `Bearer ${token}`);
			}
		}

		return headers;
	},
});

const baseQueryWithReauth: BaseQueryFn = async (args, api, extraOptions) => {
	const result = await baseQuery(args, api, extraOptions);

	if (result.error) {
		const status = Number(result?.error?.status);

		// hacky code - display a custom error upon a failed request submission
		// to lead user out of the hanging state
		if (args.url === 'requests' && args.method === 'POST') {
			LogRocket.error('req_submit_error', result.error);

			api.dispatch(
				setError({
					error: 'Request submission had an error. Please return to your dashboard to confirm its status.',
					status,
				}),
			);
		} else {
			if (status >= 500 && status <= 599) {
				LogRocket.error('500', result.error);

				api.dispatch(
					setError({
						error: result?.error?.data as string,
						status,
					}),
				);
			}

			if (status === 400) {
				LogRocket.error('400', result.error);
				api.dispatch(
					setError({
						error: result?.error?.data as string,
						status,
					}),
				);
			}

			if (status === 401) {
				LogRocket.error('401', result.error);
				api.dispatch(resetAuth());
			}

			if (status === 403) {
				LogRocket.error('403', result.error);
				api.dispatch(
					setError({
						error: "You're not authorised to perform this operation.",
						status,
					}),
				);
			}
		}
	}

	return result;
};

// TODO: invalidate cached data with particular id not the entire cached data
export const ausrehabApi = createApi({
	reducerPath: 'ausrehabApi',
	tagTypes: [
		'Request',
		'EmailTemplates',
		'Tests',
		'Suppliers',
		'Users',
		'Request-Reports',
		'Bulk-Request',
		'Bulk-Request-Reports',
	],
	baseQuery: baseQueryWithReauth,
	endpoints: () => ({}),
});
